import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, History, Navigation, Calendar, FileText } from 'lucide-react';
import { OwnerLayout } from '../../components/OwnerLayout';
import api from '../../api';

const SERVICE_ICONS = {
  sound_box: '🔊', upi_qr: '📱', current_savings: '🏦',
  daily_account: '📅', fixed_deposit: '📈', recurring_deposit: '🔁',
  monthly_interest: '💵', loan: '💰', other: '📦'
};

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
}

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`} style={{ bottom: '24px' }}>{msg}</div>;
}

export default function OwnerEditMerchant() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState(null);
  const [services, setServices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({
    businessName: '', ownerName: '', phone: '', address: '', city: '',
    nextVisitDate: '', interestedServices: [], agentId: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: '' });
  const [activeTab, setActiveTab] = useState('details');
  const [newNote, setNewNote] = useState('');

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const loadData = useCallback(async () => {
    try {
      const [merchantRes, servicesRes, agentsRes] = await Promise.all([
        api.get(`/merchants/${id}`),
        api.get('/services/all'),
        api.get('/agents')
      ]);
      const m = merchantRes.data;
      setMerchant(m);
      setServices(servicesRes.data || []);
      setAgents(agentsRes.data || []);
      setForm({
        businessName: m.businessName || '',
        ownerName: m.ownerName || '',
        phone: m.phone || '',
        address: m.address || '',
        city: m.city || '',
        nextVisitDate: m.nextVisitDate || '',
        interestedServices: m.interestedServices || [],
        agentId: m.agentId || ''
      });
    } catch (err) {
      console.error(err);
      showToast('Failed to load merchant');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/merchants/${id}`, form);
      showToast('Merchant updated successfully');
      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleAddOwnerNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/merchants/${id}/owner-note`, { note: newNote });
      showToast('Owner note added');
      setNewNote('');
      loadData();
    } catch {
      showToast('Failed to add note');
    }
  };

  const handleServiceToggle = (serviceId, slug) => {
    let newSelected = [...form.interestedServices];
    if (newSelected.includes(serviceId)) {
      newSelected = newSelected.filter(sid => sid !== serviceId);
    } else {
      newSelected.push(serviceId);
      if (slug === 'upi_qr') {
        const caService = services.find(s => s.slug === 'current_savings');
        if (caService && !newSelected.includes(caService.id)) {
          newSelected.push(caService.id);
        }
      }
    }
    setForm(p => ({ ...p, interestedServices: newSelected }));
  };

  const openMaps = () => {
    if (merchant?.gps_lat && merchant?.gps_lng) {
      window.open(`https://www.google.com/maps?q=${merchant.gps_lat},${merchant.gps_lng}`, '_blank');
    }
  };

  if (loading) {
    return (
      <OwnerLayout title="Edit Merchant">
        <div className="flex items-center justify-center h-64 text-navy font-heading">Loading…</div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout title="Edit Merchant">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button className="btn-outline" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h1 className="font-heading font-bold text-navy text-lg">{merchant?.businessName}</h1>
          <p className="text-xs text-gray">{merchant?.ownerName} · {merchant?.phone}</p>
        </div>
        {merchant?.gps_lat && (
          <button className="btn-outline ml-auto" onClick={openMaps} data-testid="gps-view-btn">
            <Navigation size={14} /> View on Map
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button 
          className={`btn-outline ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button 
          className={`btn-outline ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
          data-testid="history-tab"
        >
          <History size={14} /> Visit History ({merchant?.visitLog?.length || 0})
        </button>
        <button 
          className={`btn-outline ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
          data-testid="notes-tab"
        >
          <FileText size={14} /> Owner Notes ({merchant?.ownerNotes?.length || 0})
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="panel">
          <form onSubmit={handleSubmit} className="p-4">
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Business Name *</label>
                <input
                  className="form-input"
                  value={form.businessName}
                  onChange={e => setForm(p => ({ ...p, businessName: e.target.value }))}
                  required
                  data-testid="edit-business-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Owner Name *</label>
                <input
                  className="form-input"
                  value={form.ownerName}
                  onChange={e => setForm(p => ({ ...p, ownerName: e.target.value }))}
                  required
                  data-testid="edit-owner-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone *</label>
                <input
                  className="form-input"
                  value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  required
                  inputMode="numeric"
                  data-testid="edit-phone"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Assigned Agent</label>
                <select
                  className="form-select"
                  value={form.agentId}
                  onChange={e => setForm(p => ({ ...p, agentId: e.target.value }))}
                  data-testid="edit-agent"
                >
                  <option value="">Select Agent</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  className="form-input"
                  value={form.address}
                  onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  className="form-input"
                  value={form.city}
                  onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Next Follow-up Date</label>
                <input
                  className="form-input"
                  type="date"
                  value={form.nextVisitDate}
                  onChange={e => setForm(p => ({ ...p, nextVisitDate: e.target.value }))}
                  data-testid="edit-followup-date"
                />
              </div>
            </div>

            <div className="form-group mt-4">
              <label className="form-label">Interested Services</label>
              <div className="svc-grid">
                {services.map(s => {
                  const isSelected = form.interestedServices.includes(s.id);
                  const isPrimary = s.slug === 'upi_qr';
                  return (
                    <div
                      key={s.id}
                      className={`svc-btn ${isSelected ? 'sel' : ''} ${isPrimary ? 'primary' : ''}`}
                      onClick={() => handleServiceToggle(s.id, s.slug)}
                      data-testid={`edit-service-${s.slug}`}
                    >
                      <span className="svc-icon">{SERVICE_ICONS[s.slug] || '📋'}</span>
                      <span className="svc-name">{s.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button type="submit" className="btn-navy" disabled={saving} data-testid="save-edit-btn">
                <Save size={14} style={{ marginRight: '6px' }} />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="panel p-4">
          <div className="font-heading font-bold text-navy mb-3">Visit History</div>
          {merchant?.visitLog?.length === 0 ? (
            <div className="text-center py-8 text-gray">No visits recorded yet</div>
          ) : (
            <div className="space-y-3">
              {[...(merchant?.visitLog || [])].reverse().map((visit, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg" data-testid={`visit-${idx}`}>
                  <div className="flex items-center gap-2 text-xs text-gray mb-1">
                    <Calendar size={12} />
                    {formatDate(visit.visitedAt)}
                    {visit.rescheduledTo && (
                      <span className="text-navy">→ Rescheduled to: {visit.rescheduledTo}</span>
                    )}
                  </div>
                  <div className="text-sm">{visit.note}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="panel p-4">
          {/* Add New Owner Note */}
          <div className="mb-4 pb-4 border-b border-gray-100">
            <div className="font-heading font-bold text-navy mb-2">Add Private Note</div>
            <textarea
              className="form-input mb-2"
              rows={2}
              placeholder="Add a private note (only visible to owner)…"
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              data-testid="new-owner-note"
            />
            <button className="btn-navy" onClick={handleAddOwnerNote} data-testid="add-owner-note-btn">
              Add Note
            </button>
          </div>

          {/* Owner Notes List */}
          <div className="font-heading font-bold text-navy mb-2">Owner Notes</div>
          {merchant?.ownerNotes?.length === 0 ? (
            <div className="text-center py-8 text-gray">No owner notes yet</div>
          ) : (
            <div className="space-y-3">
              {[...(merchant?.ownerNotes || [])].reverse().map((note, idx) => (
                <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid={`owner-note-${idx}`}>
                  <div className="flex items-center gap-2 text-xs text-blue-600 mb-1">
                    <FileText size={12} />
                    {formatDate(note.addedAt)}
                  </div>
                  <div className="text-sm">{note.note}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Toast msg={toast.msg} show={toast.show} />
    </OwnerLayout>
  );
}
